import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  LandingContainer,
  Section,
  Container,
  AnimateOnScroll,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  landingHeroStyles,
} from '../home/HomePage.styles';
import CapabilityShowcase from '../home/CapabilityShowcase';
import CoreCapabilities, { CtaRow, FaqList, SurfaceLink, WorkflowContrast } from './CoreCapabilities';
import { useScrollReveal } from '../../shared/components/useScrollReveal';
import ConnectorGlobe from '../../shared/components/ConnectorGlobe';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { Footer } from '../../shared/components/Footer';
import SEO from '../../shared/components/SEO';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';

// =============================================================================
// PRODUCT PAGE (/product)
// -----------------------------------------------------------------------------
// The product deep-dive for the PowerPoint surface. Page spine:
//   hero (headline, subhead, ONE primary CTA, proof, demo video)
//   -> problem to solution -> how it works -> capabilities -> connectors
//   -> FAQ -> risk reversal + closing CTA (same CTA wording as the hero).
// The core-capabilities bento is the shared CoreCapabilities component (the
// same one /excel and /word use) so the three surfaces cannot drift apart.
// House style: no em dashes.
// =============================================================================

const ACCENT = '#B83B1D';
const INK = '#2E140A';

// One offer, one action. The same label is used in the hero and in the closing
// band so the page asks for exactly one thing, twice.
const PRIMARY_CTA_LABEL = 'See it on your own deck';

// -----------------------------------------------------------------------------
// PROBLEM TO SOLUTION.
// The recurring deliverable cycle described as it actually runs, then the same
// cycle with Restack in it. The contrast lives in the steps, not in invented
// numbers: there are no metrics here on purpose.
// -----------------------------------------------------------------------------
const workflowContrast = [
  {
    step: 'Research',
    before: 'Someone reads the filings, the reports, and the terminal exports, then retypes the figures that matter into the deck.',
    after: 'Restack reads the same sources and writes the figures in, each one carrying the page and paragraph it came from.',
  },
  {
    step: 'Build',
    before: 'The chart grammar the deck runs on, waterfalls, Mekkos, bridges, gets rebuilt shape by shape every cycle.',
    after: 'The same charts render natively in your slide, editable afterwards like any other PowerPoint chart.',
  },
  {
    step: 'Format',
    before: 'A generic AI draft lands off-template, so the evening goes on fonts, palette, margins, and the disclaimer.',
    after: "Restack reads your slide master and holds it, so what lands is already on your firm's template.",
  },
  {
    step: 'Defend',
    before: 'When the committee asks where a number came from, the trail is somewhere in a downloads folder.',
    after: 'Every figure traces back to its source, so the answer is one click rather than one afternoon.',
  },
];

// -----------------------------------------------------------------------------
// FAQ. The objections a deal team raises before it trials anything, pulled by id
// from FAQ_REPOSITORY so this page and the support widget serve the same answers.
// The `refund` entry carries the risk reversal (30-day money-back guarantee).
// -----------------------------------------------------------------------------
const PRODUCT_FAQ_IDS = [
  'what-is-restack',
  'how-works',
  'different-from-chatgpt',
  'research-quality',
  'frameworks',
  'powerpoint-versions',
  'security',
  'collaboration',
  'refund',
];
const PRODUCT_FAQ = PRODUCT_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

// FAQPage structured data, so answer engines can quote these directly.
const PRODUCT_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: PRODUCT_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

// The risk reversal, verbatim from the same repository entry the FAQ renders.
const REFUND_COPY = FAQ_REPOSITORY.find((entry) => entry.id === 'refund')?.answer || '';

export default function ProductPage() {
  const theme = useTheme();
  const hair = alpha(INK, 0.09);

  // Demo video state.
  const demoVideoRef = useRef(null);
  const [demoVideoPlaying, setDemoVideoPlaying] = useState(false);
  const handlePlayDemo = useCallback(() => {
    const video = demoVideoRef.current;
    if (!video) return;
    video.play()?.catch(() => {});
    setDemoVideoPlaying(true);
  }, []);

  // Scroll reveal for the animated blocks on this page.
  const isVisible = useScrollReveal();

  // Scroll to a hash section (e.g. the header "Connectors" item links to
  // /product#connectors) on load and on hash change.
  const location = useLocation();
  useEffect(() => {
    const id = (location.hash || '').replace('#', '');
    if (!id) return undefined;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <LandingContainer data-theme="light">
      <SEO
        title="Product | AI for Private Equity"
        description="The deliverable engine for Private Equity. Watch it research and build a client-ready deck on your firm's template, every figure cited, working in your live Office files."
        canonical="/product"
        schema={PRODUCT_FAQ_SCHEMA}
      />
      <style>{landingHeroStyles}</style>

      <SiteHeader offsetTop={0} />

      <Box component="main" id="main-content">
        {/* 1. HERO: outcome headline, subhead, one primary CTA plus the
            Microsoft Store credential, then the demo video. The extra top
            padding clears the fixed header. */}
        <Section id="product-demo" sx={{ pt: { xs: 11, md: 16 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('product-hero-header')} data-animate-id="product-hero-header">
              <CenteredHeader sx={{ mb: { xs: 3, md: 4 } }}>
                <SectionEyebrow>Product</SectionEyebrow>
                <SectionTitle component="h1">From prompt to a committee-ready deliverable. <em>Built for Private Equity.</em></SectionTitle>
                <SectionSubtitle>
                  Restack runs the deck workflow from research and source material to a client-ready deliverable
                  on your firm's template, with figures cited inside your live Office files.
                </SectionSubtitle>
              </CenteredHeader>
              {/* The CTA sits outside CenteredHeader: that component forces
                  `margin: 0 auto` on every direct child, which would fight the
                  row's own vertical spacing. */}
              <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} sx={{ mb: 1.75 }} />
              {/* Team access is a conversation, not a published seat number.
                  This line carries the row's old bottom margin so the spacing
                  down to the demo visual is unchanged. */}
              <Typography
                sx={{
                  textAlign: 'center',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: { xs: '0.85rem', md: '0.9rem' },
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: alpha(theme.palette.text.primary, 0.58),
                  mb: { xs: 6, md: 8 },
                }}
              >
                For team access,{' '}
                <Box
                  component="a"
                  href="mailto:hi@re-stack.com"
                  sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    textDecorationColor: alpha(theme.palette.text.primary, 0.3),
                    textUnderlineOffset: '2px',
                    transition: 'color 0.2s ease, text-decoration-color 0.2s ease',
                    '&:hover': { color: '#B83B1D', textDecorationColor: '#B83B1D' },
                  }}
                >
                  get in touch
                </Box>
                .
              </Typography>
            </AnimateOnScroll>

            <AnimateOnScroll isVisible={isVisible('product-hero-visual')} delay={0.1} data-animate-id="product-hero-visual">
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 1100,
                  margin: '0 auto',
                  aspectRatio: '3566 / 2324',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: '#E8E5E1',
                  boxShadow: '0 24px 60px rgba(46, 20, 10, 0.18), 0 8px 20px rgba(46, 20, 10, 0.10), 0 0 0 1px rgba(46, 20, 10, 0.06)',
                  transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease',
                  '&:hover .demo-play-button': demoVideoPlaying ? {} : {
                    transform: 'translate(-50%, -50%) scale(1.06)',
                    background: ACCENT,
                  },
                }}
              >
                <Box
                  component="video"
                  ref={demoVideoRef}
                  src="/videos/restack-demo.mp4"
                  preload="metadata"
                  playsInline
                  controls={demoVideoPlaying}
                  aria-label="Restack building a client-ready slide inside PowerPoint"
                  onPlay={() => setDemoVideoPlaying(true)}
                  onClick={demoVideoPlaying ? undefined : handlePlayDemo}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: '20px',
                    cursor: demoVideoPlaying ? 'default' : 'pointer',
                    background: '#E8E5E1',
                  }}
                />

                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '20px',
                    border: '1px solid #E8E5E1',
                    pointerEvents: 'none',
                  }}
                />

                {!demoVideoPlaying && (
                  <Box
                    component="button"
                    type="button"
                    className="demo-play-button"
                    onClick={handlePlayDemo}
                    aria-label="Play Restack demo video"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 84,
                      height: 84,
                      borderRadius: '50%',
                      background: 'rgba(184, 59, 29, 0.92)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35), 0 0 0 6px rgba(255, 255, 255, 0.18)',
                      transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), background 0.2s ease',
                      padding: 0,
                      '&:hover': { background: ACCENT },
                      '&:active': { transform: 'translate(-50%, -50%) scale(0.97)' },
                      '&:focus-visible': { outline: '3px solid #ffffff', outlineOffset: 4 },
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      component="span"
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: '14px solid transparent',
                        borderBottom: '14px solid transparent',
                        borderLeft: '22px solid #ffffff',
                        marginLeft: '5px',
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    padding: '0.4rem 0.8rem',
                    borderRadius: '12px',
                    background: 'rgba(0, 0, 0, 0.55)',
                    backdropFilter: 'blur(8px)',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    letterSpacing: '-0.005em',
                    pointerEvents: 'none',
                  }}
                >
                  <Box aria-hidden="true" component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT }} />
                  {demoVideoPlaying ? 'Sped up' : 'Watch the 1-minute demo'}
                </Box>
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 2. PROBLEM TO SOLUTION. Where the hours go in the recurring deck
            cycle, and which of those steps Restack takes off the pile. */}
        <Section id="the-rebuild">
          <Container>
            <AnimateOnScroll isVisible={isVisible('rebuild-header')} data-animate-id="rebuild-header">
              <CenteredHeader>
                <SectionEyebrow>The rebuild</SectionEyebrow>
                <SectionTitle component="h2">The same deck, <em>rebuilt every cycle</em>.</SectionTitle>
                <SectionSubtitle>
                  The deliverable barely changes between quarters. The work of producing it never gets shorter.
                  Here is where the hours go, and what changes with Restack in the file.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <WorkflowContrast rows={workflowContrast} accent={ACCENT} theme={theme} hair={hair} isVisible={isVisible} />
          </Container>
        </Section>

        {/* 3. HOW IT WORKS. The four proof cards, always-on animated. */}
        <CapabilityShowcase isVisible={isVisible} loop />

        {/* 4. CORE CAPABILITIES. The shared bento, PowerPoint variant. */}
        <CoreCapabilities app="powerpoint" isVisible={isVisible} />

        {/* 5. CONNECTS TO. The data-sources globe (same section as the
            solutions pages). ConnectorGlobe is self-contained (propless). */}
        <Section id="connectors" sx={{ scrollMarginTop: { xs: '76px', md: '96px' } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('connectors-header')} data-animate-id="connectors-header">
              <CenteredHeader>
                <SectionEyebrow>Connects to</SectionEyebrow>
                <SectionTitle component="h2">The deck, built from your <em>data sources.</em></SectionTitle>
                <SectionSubtitle>
                  Restack reads the sources you point it at: market terminals, document stores, and internal
                  drives, all under your access.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <ConnectorGlobe />
          </Container>
        </Section>

        {/* 6. FAQ. */}
        <Section id="faq">
          <Container>
            <AnimateOnScroll isVisible={isVisible('faq-header')} data-animate-id="faq-header">
              <CenteredHeader>
                <SectionEyebrow>Questions</SectionEyebrow>
                <SectionTitle component="h2">Before you <em>trial it</em>.</SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('faq-list')} data-animate-id="faq-list">
              <FaqList entries={PRODUCT_FAQ} theme={theme} />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 7. CLOSING CTA. Same wording as the hero, with the risk reversal
            directly under it. */}
        <Section id="product-cta" sx={{ pb: { xs: 8, md: 12 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('product-cta')} data-animate-id="product-cta">
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  borderRadius: '20px',
                  textAlign: 'center',
                  border: `1px solid ${hair}`,
                  background: `linear-gradient(135deg, ${alpha(ACCENT, 0.06)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
                }}
              >
                <Typography component="h2" sx={{ fontFamily: theme.typography.fontFamily, fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.02em', color: 'text.primary', mb: 1.5 }}>
                  Put it on the deck you are building this week.
                </Typography>
                <Typography sx={{ maxWidth: 560, mx: 'auto', mb: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.95rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  Bring a real slide and a real source pack. We will show you Restack researching, building, and
                  citing it inside your live PowerPoint file, on your firm's template.
                </Typography>
                <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} />
                <Typography sx={{ maxWidth: 560, mx: 'auto', mt: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  {REFUND_COPY}
                </Typography>
                {/* Cross-surface navigation stays a text link, not a second
                    button, so the band keeps exactly one primary action. */}
                <Typography sx={{ mt: 2, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', color: 'text.secondary' }}>
                  Also for <SurfaceLink to="/excel">Excel</SurfaceLink> and <SurfaceLink to="/word">Word</SurfaceLink>.
                </Typography>
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
